import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import firebase from 'firebase/compat/app';
import { ApisauceInstance } from 'apisauce';

import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import { AUTH_ERROR_MSG_KEY } from '../../../../screens/Login/ErrorBanner';
import { clearCachedSelectedAccountId } from '../AccountPickerProvider';
import env from 'env/.public-env.json';
import captureException from 'services/captureException';
import { VIEW_AS_USER_ID_HEADER } from 'services/buildCloudFunctionClient';

interface AccessProfile {
  accounts: { [accountId: string]: { permissions: string[]; roles: string[] } };
}

interface ApiUserAccess {
  permissions: FleetOps.Permission[];
  userProfile?: {
    permissions: string[];
    roles: FleetOps.Role[];
  };
}

const useCurrentPermissions = (api: ApisauceInstance | undefined) => {
  const { impersonatorId } = useContext(ImpersonatorContext);
  const currentUser = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [permissions, setPermissions] = useState<FleetOps.Permission[]>([]);
  const [apiUserAccess, setApiUserAccess] = useState<
    ApiUserAccess | undefined
  >();
  const [accessProfile, setAccessProfile] = useState<
    AccessProfile | undefined
  >();

  const fetchUserAccess = useCallback(async (): Promise<ApiUserAccess> => {
    if (!api) {
      return { permissions: [] };
    }
    const baseUrl = env.PREVIEW
      ? '/v1/preview/user_permissions'
      : '/v1/user_permissions';
    if (impersonatorId) {
      // "View app as": the permissions-service authorizes a request carrying
      // the view-as header as the impersonated user, so the plain endpoint
      // returns their permissions and profile. Don't use /users/:userId here —
      // it requires a user-management permission, which the impersonated user
      // usually lacks, so it 403s for anyone but an Admin (see ENG-1334).
      const response = await api.get<
        {
          permissions: FleetOps.Permission[];
          accountProfile?: ApiUserAccess['userProfile'];
        },
        { error: string }
      >(baseUrl, {}, { headers: { [VIEW_AS_USER_ID_HEADER]: impersonatorId } });

      if (response.ok && !!response.data) {
        return {
          permissions: response.data.permissions,
          userProfile: response.data.accountProfile,
        };
      }

      const error = new Error(
        `Could not load permissions to view app as ${impersonatorId} (${response.status})`,
      );
      error.name = 'View app as: permissions request failed';
      captureException(error);
    } else {
      const response = await api.get<
        { permissions: FleetOps.Permission[] },
        { error: string }
      >(baseUrl);
      if (response.ok && !!response.data) {
        return { permissions: response.data.permissions };
      } else if (!response.ok) {
        if (response.data && response.data.error) {
          const error = response.data.error;
          let errorMessage;
          if (error === 'Forbidden: Session identity provider not permitted') {
            errorMessage =
              'Your organization has enabled SSO. Please' +
              ' sign in with Microsoft';
          } else if (
            error === 'Forbidden: Access not granted to this account'
          ) {
            errorMessage =
              'You do not have access to FleetOps. Contact your IT admin';
          }

          if (errorMessage) {
            window.localStorage.setItem(AUTH_ERROR_MSG_KEY, errorMessage);
            clearCachedSelectedAccountId();
          }
          await firebase.auth().signOut();
        }
      } else {
        await firebase.auth().signOut();
      }
    }

    return { permissions: [] };
  }, [api, impersonatorId]);

  useEffect(() => {
    // We need to know when the current user's roles and permissions change
    const listener = firebase
      .firestore()
      .collection('userProfiles')
      .doc(currentUser.id)
      .onSnapshot((doc) => {
        if (doc.exists) {
          const newProfile = doc.data() as AccessProfile;
          setAccessProfile(newProfile);
        }
      });
    return () => {
      listener();
    };
  }, [currentUser.id]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    fetchUserAccess().then((access) => {
      if (isActive) {
        setPermissions(access.permissions);
        setApiUserAccess(access);
        setIsLoading(false);
      }
    });

    return () => {
      isActive = false;
    };
  }, [api, accessProfile, fetchUserAccess]);

  const currentRoles = useMemo((): FleetOps.Role[] => {
    if (impersonatorId) {
      return apiUserAccess?.userProfile?.roles ?? [];
    }
    const accountAccess = accessProfile?.accounts[selectedAccountId];
    return (accountAccess?.roles ?? []) as FleetOps.Role[];
  }, [impersonatorId, apiUserAccess, accessProfile, selectedAccountId]);

  return {
    currentPermissions: permissions,
    currentRoles,
    isLoading,
  };
};

export default useCurrentPermissions;
